import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import styles from "./SingleRandBView.module.css";
import { useTypewriter, Cursor, Typewriter } from "react-simple-typewriter";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Loading from "../../components/Loading/Loading";
import { toast } from "react-toastify";
import { smTypes } from "../../util/flStates";
import { useAuth } from "../../contexts/AuthContext";
import DashProListings from "../../components/dashbaord/DashProListings/DashProListings";
import ShowProListings from "./ShowProListings/ShowProListings";

// NEW: safe rendering helpers
import DOMPurify from "dompurify";

// allow a small, safe subset of inline styles (so colors/highlights survive)
const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "hr",
    "code",
    "span",
    "mark",
  ],
  ALLOWED_ATTR: ["href", "rel", "target", "style", "class"],
};
if (typeof window !== "undefined" && !window.__PURIFY_STYLE_HOOK_ATTACHED__) {
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const allowed = [
        "color",
        "background-color",
        "text-decoration",
        "font-weight",
        "font-style",
      ];
      const safe = (data.attrValue || "")
        .split(";")
        .map((s) => s.trim())
        .filter((s) => {
          const [prop] = s.split(":").map((x) => x.trim().toLowerCase());
          return allowed.includes(prop);
        })
        .join("; ");
      if (safe) data.attrValue = safe;
      else data.keepAttr = false;
    }
  });
  window.__PURIFY_STYLE_HOOK_ATTACHED__ = true;
}

// Fallback: convert plain TEXT to simple HTML (preserve \n and linkify)
const textToDisplayHtml = (text) => {
  if (!text) return "";
  const withLinks = String(text).replace(
    /(https?:\/\/[^\s)]+)(?![^<]*>)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  return withLinks.replace(/\n/g, "<br/>");
};

const SingleRandBView = () => {
  const navigate = useNavigate();
  const { accessToken, showLoginModal } = useAuth();
  const { proSlug } = useParams();
  const [professional, setProfessional] = useState(null);

  const [reels, setReels] = useState([]);
  const [youtubeVideos, setYouTubeVideos] = useState([]);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [spotifyPodcasts, setSpotifyPodcasts] = useState([]);
  const [blogs, setBlogs] = useState([]);

  const [brokerStates, setBrokerStates] = useState([]);
  const [realtorStates, setRealtorStates] = useState([]);
  const [verifiedBrokerStates, setVerifiedBrokerStates] = useState([]);
  const [verifiedRealtorStates, setVerifiedRealtorStates] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [specializationArray, setSpecializationArray] = useState([
    "Professional",
    "Real Estate",
  ]);

  const handleClickContact = () => {
    if (!accessToken) {
      showLoginModal();
    }
    navigate(`/MyFirstLand?category=messages`, {
      state: {
        targetUser: {
          id: professional.user_id,
          first_name: professional.first_name,
          last_name: professional.last_name,
          photo_url: professional.photo_url,
          tags: "Realtor, Broker",
        },
      },
    });
  };

  useEffect(() => {
    if (
      proSlug &&
      professional &&
      "specialization" in professional &&
      professional.specialization
    ) {
      setSpecializationArray(professional.specialization.split(", "));
    }
  }, [proSlug, professional]);

  useEffect(() => {
    const fetchProfessionalData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/professional/profile/${proSlug}`
        );

        if (response?.status === 200 && response.data?.success) {
          const pro = response.data.proData || {};

          const licenses = pro.licenses || [];
          const brokerLicenses = licenses.filter(
            (l) => l.license_type === "broker"
          );
          const realtorLicenses = licenses.filter(
            (l) => l.license_type === "realtor"
          );

          setBrokerStates(brokerLicenses.map((l) => l.state));
          setRealtorStates(realtorLicenses.map((l) => l.state));
          setVerifiedBrokerStates(
            brokerLicenses.filter((l) => l.is_verified).map((l) => l.state)
          );
          setVerifiedRealtorStates(
            realtorLicenses.filter((l) => l.is_verified).map((l) => l.state)
          );

          setProfessional(pro);
        }
      } catch (err) {
        console.error(
          "Error occurred while fetching professional profile data: ",
          err
        );
        toast.error("Unable to get the professional data. Please try again!");
      } finally {
        setIsLoading(false);
      }
    };

    if (proSlug) {
      fetchProfessionalData();
    }
  }, [proSlug]);

  // lazy load social media
  useEffect(() => {
    const fetchSocialMediaData = async () => {
      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/professional/profile/${proSlug}/sm`
        );

        if (
          response?.status === 200 &&
          response.data?.success &&
          response.data.proSMLinks
        ) {
          const liveYouTubeVideos = [];
          const liveInstagramPosts = [];
          const liveSpotifyPodcasts = [];
          const liveInstagramReels = [];

          response.data.proSMLinks.forEach((smLink) => {
            if (smLink.type_sm_id && smLink.url) {
              switch (smLink.type_sm_id) {
                case smTypes.instagramPost:
                  liveInstagramPosts.push(smLink);
                  break;
                case smTypes.instagramReel:
                  liveInstagramReels.push(smLink);
                  break;
                case smTypes.spotifyPodcast:
                  liveSpotifyPodcasts.push(smLink);
                  break;
                case smTypes.youtubeShort:
                  liveYouTubeVideos.push(smLink);
                  break;
                default:
                  console.warn("Unknown social media type:", smLink.type_sm_id);
              }
            } else {
              console.warn("Invalid social media link:", smLink);
            }
          });

          setYouTubeVideos(liveYouTubeVideos);
          setInstagramPosts(liveInstagramPosts);
          setSpotifyPodcasts(liveSpotifyPodcasts);
          setReels(liveInstagramReels);
        } else {
          console.error("Invalid response or no social media links found.");
        }
      } catch (err) {
        console.error("Unable to fetch social media links: ", err);
        toast.error("Unable to fetch social media data. Please try again!");
      }
    };

    if (proSlug && professional && professional.id) {
      fetchSocialMediaData();
    }
  }, [proSlug, professional?.id]);

  // Lazy load blogs
  useEffect(() => {
    const fetcBlogs = async () => {
      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/professional/profile/${proSlug}/blogs`
        );

        if (
          response?.status === 200 &&
          response.data?.success &&
          response.data.proBlogs
        ) {
          setBlogs(response.data.proBlogs || []);
        } else {
          console.error("Invalid response or no blogs found.");
        }
      } catch (err) {
        console.error("Unable to fetch blogs: ", err);
        toast.error("Unable to fetch blogs. Please try again!");
      }
    };

    if (proSlug && professional && professional.id) {
      fetcBlogs();
    }
  }, [proSlug, professional?.id]);

  const recordView = async (mediaType, proId, url) => {
    try {
      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/dash/dashboard-insights/social-media-views`,
        { pro_id: proId, media_type: mediaType, view_count: 1 }
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error recording view:", error);
      toast.error("Failed to record view.");
    }
  };

  // Build safe bio HTML once we have professional data
  const bioHtml = React.useMemo(() => {
    if (!professional) return "";
    if (professional.bio_html) {
      return DOMPurify.sanitize(professional.bio_html, SANITIZE_OPTS);
    }
    if (professional.bio) {
      return textToDisplayHtml(professional.bio);
    }
    return "";
  }, [professional]);

  return (
    <>
      <Navbar />
      <div>
        {isLoading ? (
          <div className="loading-container">
            <Loading />
          </div>
        ) : (
          <div>
            {professional ? (
              <div className={styles.container}>
                <div className={styles.card}>
                  <div className={styles.heroSection}>
                    <div className={styles.textContainer}>
                      <h1 className={styles.name}>
                        Hi, I'm {professional.first_name}{" "}
                        {professional.last_name}
                      </h1>
                      <h2 className={styles.specialization}>
                        <Typewriter
                          words={
                            specializationArray
                              ? specializationArray
                              : ["Professional", "Real Estate"]
                          }
                          loop={5}
                          cursor
                          cursorStyle="_"
                          typeSpeed={70}
                          deleteSpeed={50}
                          delaySpeed={1000}
                        />
                        <Cursor cursorStyle="|" />
                      </h2>

                      {/* Removed editable bio here */}

                      <button
                        className={styles.contactButton}
                        onClick={handleClickContact}
                      >
                        Contact Me
                      </button>
                    </div>

                    <div className={styles.imageContainer}>
                      <div className={styles.imageBackground}></div>

                      {verifiedRealtorStates.length > 0 &&
                      verifiedBrokerStates.length > 0 ? (
                        <span
                          className={`${styles.badge} ${styles.realtorBadge}`}
                        >
                          Verified Realtor and Broker
                        </span>
                      ) : verifiedRealtorStates.length > 0 ? (
                        <span
                          className={`${styles.badge} ${styles.realtorBadge}`}
                        >
                          Verified Realtor
                        </span>
                      ) : verifiedBrokerStates.length > 0 ? (
                        <span
                          className={`${styles.badge} ${styles.brokerBadge}`}
                        >
                          Verified Broker
                        </span>
                      ) : (
                        <span
                          className={`${styles.badge} ${styles.notVerified}`}
                        >
                          Not a Verified Agent
                        </span>
                      )}

                      <img
                        src={professional.photo_url}
                        alt=""
                        className={styles.profileImage}
                      />
                    </div>
                  </div>

                  <div className={styles.licenseBox}>
                    <div className={styles.licenseBoxHeader}>
                      <strong>
                        <h3>Licenses:</h3>
                      </strong>
                    </div>

                    {verifiedBrokerStates?.length > 0 && (
                      <p>
                        <strong>Broker in:</strong>{" "}
                        {verifiedBrokerStates.join(", ")}
                      </p>
                    )}
                    {verifiedRealtorStates?.length > 0 && (
                      <p>
                        <strong>Realtor in:</strong>{" "}
                        {verifiedRealtorStates.join(", ")}
                      </p>
                    )}
                  </div>

                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <strong>Languages:</strong> {professional.languages}
                    </div>
                    <div className={styles.infoItem}>
                      <strong>Experience:</strong>{" "}
                      {professional.years_of_experience} years
                    </div>
                    <div className={styles.infoItem}>
                      <strong>Active Listings:</strong>{" "}
                      {professional.active_listings_cnt}
                    </div>
                  </div>

                  {/* READ-ONLY formatted bio */}
                  <div className={styles.biography}>
                    <h2 className={styles.title}>About Me</h2>
                    {bioHtml ? (
                      <div
                        className={styles.bioDescription} // styled to preserve lists/lines
                        dangerouslySetInnerHTML={{ __html: bioHtml }}
                      />
                    ) : (
                      <p className={styles.bioDescription} />
                    )}
                  </div>

                  <ShowProListings
                    accessToken={accessToken}
                    userId={professional.user_id}
                  />

                  <div className={styles.mediaSection}>
                    <h2 className={styles.sectionTitle}>Instagram Reels</h2>
                    <div className={styles.mediaGrid}>
                      {reels.map((reel, index) => (
                        <div
                          key={index}
                          className={styles.clickableIframeContainer}
                          onClick={() =>
                            recordView(
                              smTypes.instagramReel,
                              reel.pro_id,
                              reel.url
                            )
                          }
                        >
                          <iframe
                            className={styles.mediaItem}
                            src={`https://www.instagram.com/p/${reel.url
                              .split("/")
                              .slice(-2, -1)}/embed/`}
                            frameBorder="0"
                            allowFullScreen
                          ></iframe>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.mediaSection}>
                    <h2 className={styles.sectionTitle}>YouTube Videos</h2>
                    <div className={styles.mediaGrid}>
                      {youtubeVideos?.map((video, index) => {
                        const videoId = video.url.includes("v=")
                          ? video.url.split("v=")[1].split("&")[0]
                          : video.url.split("/").pop();
                        const embedUrl = `https://www.youtube.com/embed/${videoId}`;

                        return (
                          <div
                            key={index}
                            className={styles.clickableIframeContainer}
                            onClick={() =>
                              recordView(
                                smTypes.youtubeShort,
                                video.pro_id,
                                video.url
                              )
                            }
                          >
                            <iframe
                              className={styles.mediaItem}
                              src={embedUrl}
                              frameBorder="0"
                              allowFullScreen
                            ></iframe>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.mediaSection}>
                    <h2 className={styles.sectionTitle}>Instagram Posts</h2>
                    <div className={styles.mediaGrid}>
                      {instagramPosts?.map((post, index) => (
                        <div
                          key={index}
                          className={styles.clickableIframeContainer}
                          onClick={() =>
                            recordView(
                              smTypes.instagramPost,
                              post.pro_id,
                              post.url
                            )
                          }
                        >
                          <iframe
                            className={styles.mediaItem}
                            src={`https://www.instagram.com/p/${post.url
                              .split("/")
                              .slice(-2, -1)}/embed/`}
                            frameBorder="0"
                            allowFullScreen
                          ></iframe>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.mediaSection}>
                    <h2 className={styles.sectionTitle}>Spotify Podcasts</h2>
                    <div className={styles.mediaGrid}>
                      {spotifyPodcasts?.map((podcast, index) => (
                        <div
                          key={index}
                          className={styles.clickableIframeContainer}
                          onClick={() =>
                            recordView(
                              smTypes.spotifyPodcast,
                              podcast.pro_id,
                              podcast.url
                            )
                          }
                        >
                          <iframe
                            className={styles.mediaItem}
                            src={podcast.url}
                            frameBorder="0"
                            allow="encrypted-media"
                            allowFullScreen
                          ></iframe>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.mediaSection}>
                    <h2 className={styles.sectionTitle}>Blogs</h2>
                    <div className={styles.blogsGrid}>
                      {blogs.map((blog, index) => (
                        <div key={index} className={styles.blogItem}>
                          <h3 className={styles.blogTitle}>{blog.title}</h3>
                          <img src={blog.image_url} width={300} height={200} />
                          <p className={styles.blogDate}>{blog.created_at}</p>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: blog.description,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>Unable to fetch professional data!</div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default SingleRandBView;
